import React, { useEffect, useState } from "react";

import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

export default function MyOrders() {

  const { user } = useAuth();

  /* =========================================================
     STATUS FILTERS
  ========================================================= */

  const STATUS_OPTIONS = [
    "New",
    "Processing",
    "Packed",
    "Ready for delivery",
    "Delivered",
    "Cancelled",
  ];

  const [filter, setFilter] = useState("All");

  const [orders, setOrders] = useState([]);

  const [loading, setLoading] = useState(true);

  const [receivingOrderId, setReceivingOrderId] =
    useState(null);


  /* =========================================================
     LOAD CUSTOMER ORDERS
  ========================================================= */

  useEffect(() => {

    if (!user) {
      setOrders([]);
      setLoading(false);
      return;
    }

    /*
     * Match the mobile format stored in Firestore.
     */

    const mobile =
      (user.phoneNumber || "")
        .replace(/\s/g, "")
        .replace(/^\+91/, "+91");


    /*
     * Query customer's orders.
     */

    const q = query(
      collection(db, "shopOrders"),
      where("mobile", "==", mobile)
    );


    /*
     * Listen for real-time order updates.
     */

    const unsubscribe = onSnapshot(
      q,

      (snapshot) => {

        const list =
          snapshot.docs.map((orderDoc) => ({
            id: orderDoc.id,
            ...orderDoc.data(),
          }));


        /*
         * Latest orders first.
         */

        list.sort((a, b) => {

          const ta =
            a.createdAt?.seconds || 0;

          const tb =
            b.createdAt?.seconds || 0;

          return tb - ta;

        });


        setOrders(list);

        setLoading(false);

      },

      (err) => {

        console.error(
          "MyOrders error:",
          err
        );

        setOrders([]);

        setLoading(false);

      }
    );


    return unsubscribe;

  }, [user]);


  /* =========================================================
     FILTER ORDERS
  ========================================================= */

  const filteredOrders =
    filter === "All"
      ? orders
      : orders.filter(
          (order) =>
            (order.status || "New") === filter
        );


  /* =========================================================
     FORMAT DATE
  ========================================================= */

  const formatDate = (timestamp) => {

    if (!timestamp) {
      return "";
    }

    if (timestamp.toDate) {

      return timestamp
        .toDate()
        .toLocaleString();

    }

    if (timestamp.seconds) {

      return new Date(
        timestamp.seconds * 1000
      ).toLocaleString();

    }

    return "";

  };


  /* =========================================================
     PARSE ORDER ITEMS
  ========================================================= */

  const renderItems = (items) => {

    if (!items) {
      return [];
    }

    try {

      const parsed =
        typeof items === "string"
          ? JSON.parse(items)
          : items;

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed;

    } catch {

      return [];

    }

  };


  /* =========================================================
     STATUS COLOR
  ========================================================= */

  const statusColor = (status) => {

    switch (status) {

      case "Delivered":
        return "#16a34a";

      case "Cancelled":
        return "#dc2626";

      case "Ready for delivery":
        return "#2563eb";

      case "Packed":
        return "#7c3aed";

      case "Processing":
        return "#f59e0b";

      default:
        return "#6b7280";

    }

  };


  /* =========================================================
     CHECK WHETHER CUSTOMER NEEDS TO CONFIRM RECEIPT
  ========================================================= */

  const needsReceiptConfirmation = (order) => {

    return (
      order.deliveryStatus ===
        "DELIVERED" &&
      order.customerReceived !== true
    );

  };


  /* =========================================================
     CUSTOMER MARKS ORDER AS RECEIVED
  ========================================================= */

  const markOrderReceived = async (order) => {

    /*
     * Basic validation.
     */

    if (
      !user ||
      !order ||
      !order.id
    ) {
      return;
    }


    /*
     * Make sure the delivery person has
     * actually marked the order as delivered.
     */

    if (
      order.deliveryStatus !==
      "DELIVERED"
    ) {

      alert(
        "This order has not been marked as delivered by the delivery person yet."
      );

      return;
    }


    /*
     * Already confirmed.
     */

    if (
      order.customerReceived === true
    ) {
      return;
    }


    /*
     * Prevent double clicking.
     */

    if (
      receivingOrderId === order.id
    ) {
      return;
    }


    /*
     * Ask customer for confirmation.
     */

    const confirmed =
      window.confirm(
        `Confirm that you received order ${order.orderId}?`
      );


    if (!confirmed) {
      return;
    }


    try {

      setReceivingOrderId(order.id);


      /*
       * =====================================================
       * SHOP ORDER REFERENCE
       * =====================================================
       */

      const orderRef =
        doc(
          db,
          "shopOrders",
          order.id
        );


      /*
       * =====================================================
       * UPDATE SHOP ORDER
       *
       * THIS IS THE FINAL CUSTOMER CONFIRMATION.
       * =====================================================
       */

      await updateDoc(orderRef, {
  status: "Delivered",

  deliveryStatus: "DELIVERED",

  customerReceived: true,

  customerReceivedAt:
    serverTimestamp(),

  updatedAt:
    serverTimestamp(),
});


      /*
       * =====================================================
       * MARK DELIVERY CONFIRMATION NOTIFICATION AS READ
       * =====================================================
       *
       * DeliveryDashboard creates this notification using:
       *
       * `${deliveryRequestId}_delivery_confirmation`
       *
       * We use the order's deliveryRequestId when available.
       *
       * If it is not available on the shop order, we simply
       * skip this part.
       */

      const deliveryRequestId =
        order.deliveryRequestId;


      if (deliveryRequestId) {

        try {

          const notificationRef =
            doc(
              db,
              "activity",
              `${deliveryRequestId}_delivery_confirmation`
            );


          await updateDoc(
            notificationRef,
            {

              read:
                true,

              readAt:
                serverTimestamp(),

              updatedAt:
                serverTimestamp(),

            }
          );

        } catch (
          notificationError
        ) {

          /*
           * Notification cleanup failing should NOT
           * make the order confirmation fail.
           */

          console.warn(
            "Could not mark delivery notification as read:",
            notificationError
          );

        }

      }


      /*
       * SUCCESS
       */

      alert(
        "Order received successfully. Thank you!"
      );

    } catch (error) {

      console.error(
        "Customer receipt confirmation failed:",
        error
      );

      alert(
        error.message ||
        "Unable to confirm order receipt."
      );

    } finally {

      setReceivingOrderId(null);

    }

  };


  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {

    return (
      <p
        style={{
          padding: 20,
        }}
      >
        Loading orders...
      </p>
    );

  }


  /* =========================================================
     NO ORDERS
  ========================================================= */

  if (filteredOrders.length === 0) {

    return (
      <div
        style={{
          padding: 20,
          textAlign: "center",
        }}
      >

        <div
          style={{
            fontSize: 42,
            marginBottom: 10,
          }}
        >
          📦
        </div>

        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#555",
          }}
        >
          No{" "}
          {filter !== "All"
            ? ` ${filter}`
            : ""}{" "}
          orders.
        </div>

      </div>
    );

  }


  /* =========================================================
     MAIN UI
  ========================================================= */

  return (

    <div
      style={{
        padding: 16,
      }}
    >

      {/* =====================================================
          TITLE
      ===================================================== */}

      <h3
        style={{
          marginBottom: 16,
        }}
      >
        My Orders
      </h3>


      {/* =====================================================
          STATUS FILTERS
      ===================================================== */}

      <div
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          marginBottom: 16,
          paddingBottom: 4,
          scrollbarWidth: "none",
        }}
      >

        {/* ALL */}

        <button
          type="button"
          onClick={() =>
            setFilter("All")
          }
          style={{
            padding: "8px 14px",
            borderRadius: 20,
            border: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",

            background:
              filter === "All"
                ? "#2563eb"
                : "#f3f4f6",

            color:
              filter === "All"
                ? "#fff"
                : "#111",

            fontWeight: 600,
          }}
        >
          All ({orders.length})
        </button>


        {/* STATUS OPTIONS */}

        {STATUS_OPTIONS.map(
          (status) => {

            const count =
              orders.filter(
                (o) =>
                  (o.status || "New") ===
                  status
              ).length;


            return (
              <button
                key={status}
                type="button"
                onClick={() =>
                  setFilter(status)
                }
                style={{
                  padding:
                    "8px 14px",

                  borderRadius: 20,

                  border: "none",

                  cursor: "pointer",

                  whiteSpace:
                    "nowrap",

                  background:
                    filter === status
                      ? "#2563eb"
                      : "#f3f4f6",

                  color:
                    filter === status
                      ? "#fff"
                      : "#111",

                  fontWeight: 600,
                }}
              >
                {status} ({count})
              </button>
            );

          }
        )}

      </div>


      {/* =====================================================
          ORDERS
      ===================================================== */}

      {filteredOrders.map(
        (order) => {

          const items =
            renderItems(
              order.items
            );

          const needsConfirmation =
            needsReceiptConfirmation(
              order
            );

          const confirming =
            receivingOrderId ===
            order.id;


          return (

            <div
              key={order.id}
              style={{
                background: "#fff",
                borderRadius: 16,
                padding: 16,
                marginBottom: 14,
                boxShadow:
                  "0 2px 8px rgba(0,0,0,.08)",
              }}
            >

              {/* =================================================
                  ORDER ID
              ================================================= */}

              <div
                style={{
                  fontWeight: 700,
                  fontSize: 22,
                }}
              >
                {order.orderId}
              </div>


              {/* =================================================
                  ORDER DATE
              ================================================= */}

              <div
                style={{
                  fontSize: 13,
                  color: "#666",
                  marginTop: 4,
                  marginBottom: 10,
                }}
              >
                {formatDate(
                  order.createdAt
                )}
              </div>


              {/* =================================================
                  ORDER ITEMS
              ================================================= */}

              {items.map(
                (item, index) => (

                  <div
                    key={index}
                    style={{
                      marginBottom: 4,
                      fontSize: 16,
                    }}
                  >

                    •{" "}

                    {item.brand
                      ? `${item.brand} - `
                      : ""}

                    {item.name}{" "}

                    ({item.price}) x
                    {item.qty}

                  </div>

                )
              )}


             {/* =================================================
    ORDER TOTAL + DELIVERY CHARGE + DISCOUNT
================================================= */}

<div
  style={{
    marginTop: 16,
    borderTop: "1px solid #eee",
    paddingTop: 10,
  }}
>
  {/* SUBTOTAL */}

  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      fontSize: 14,
      marginBottom: 6,
      color: "#555",
    }}
  >
    <span>Subtotal</span>

    <span>
      ₹
      {Number(order.total || 0).toFixed(2)}
    </span>
  </div>

  {/* DISCOUNT */}

  {Number(
    order.discountAmount ||
      order.discount ||
      0
  ) > 0 && (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: 14,
        marginBottom: 6,
        color: "#16a34a",
        fontWeight: 600,
      }}
    >
      <span>
        Discount
        {Number(order.discountPercentage || 0) > 0
          ? ` (${order.discountPercentage}%)`
          : " (1%)"}
      </span>

      <span>
        −₹
        {Number(
          order.discountAmount ||
            order.discount ||
            0
        ).toFixed(2)}
      </span>
    </div>
  )}

  {/* DELIVERY CHARGE */}

  {Number(order.deliveryCharge || 0) > 0 && (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: 14,
        marginBottom: 6,
        color: "#555",
      }}
    >
      <span>Delivery Charge</span>

      <span>
        ₹
        {Number(
          order.deliveryCharge || 0
        ).toFixed(2)}
      </span>
    </div>
  )}

  {/* FINAL TOTAL */}

  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 10,
      marginTop: 8,
    }}
  >
    
    <div
  style={{
    fontWeight: 700,
    fontSize: 22,
  }}
>
  Total : ₹
  {(
    Number(order.total || 0) +
    Number(order.deliveryCharge || 0)
  ).toFixed(2)}
</div>


    <div
      style={{
        fontWeight: 700,
        color: needsConfirmation
          ? "#f59e0b"
          : statusColor(order.status),
        textAlign: "right",
      }}
    >
      {needsConfirmation
        ? "Awaiting receipt"
        : order.status}
    </div>
  </div>
</div>

              {/* =================================================
                  DELIVERY PERSON HAS DELIVERED
                  CUSTOMER MUST CONFIRM
              ================================================= */}

              {needsConfirmation && (

                <div
                  style={{
                    marginTop: 14,
                    padding: 14,
                    borderRadius: 12,

                    background:
                      "#fff7ed",

                    border:
                      "1px solid #fed7aa",
                  }}
                >

                  {/* MESSAGE */}

                  <div
                    style={{
                      fontWeight: 700,
                      color: "#c2410c",
                      fontSize: 16,
                      marginBottom: 6,
                    }}
                  >
                    📦 Your order has been
                    delivered
                  </div>


                  <div
                    style={{
                      fontSize: 13,
                      color: "#666",
                      lineHeight: 1.5,
                      marginBottom: 12,
                    }}
                  >
                    The delivery person has
                    marked this order as
                    delivered. Please confirm
                    that you received your
                    order.
                  </div>


                  {/* RECEIVED BUTTON */}

                  <button
                    type="button"
                    disabled={confirming}
                    onClick={() =>
                      markOrderReceived(
                        order
                      )
                    }
                    style={{
                      width: "100%",
                      border: "none",
                      borderRadius: 10,
                      padding:
                        "13px 16px",

                      background:
                        confirming
                          ? "#86efac"
                          : "#16a34a",

                      color: "#fff",

                      fontSize: 15,

                      fontWeight: 700,

                      cursor:
                        confirming
                          ? "not-allowed"
                          : "pointer",

                      display: "flex",

                      alignItems:
                        "center",

                      justifyContent:
                        "center",

                      gap: 6,

                      boxSizing:
                        "border-box",

                      opacity:
                        confirming
                          ? 0.8
                          : 1,
                    }}
                  >

                    {confirming
                      ? "CONFIRMING..."
                      : "✓ I RECEIVED MY ORDER"}

                  </button>

                </div>

              )}


              {/* =================================================
                  CUSTOMER ALREADY CONFIRMED
              ================================================= */}

              {order.customerReceived ===
                true && (

                <div
                  style={{
                    marginTop: 12,
                    padding: 10,
                    borderRadius: 10,

                    background:
                      "#dcfce7",

                    border:
                      "1px solid #bbf7d0",

                    color:
                      "#166534",

                    fontWeight: 700,

                    textAlign:
                      "center",

                    fontSize: 14,
                  }}
                >
                  ✓ Order received and
                  confirmed
                </div>

              )}

            </div>

          );

        }
      )}

    </div>

  );

}